import axios, { AxiosRequestConfig } from "axios";
import { RefreshResponse } from "../types/user.types";
import {
    clearSession,
    getRefreshToken,
    persistRefreshToken,
    persistToken,
} from "../utils/authMethods";

export const getPublicApi = () => {
    return axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
        },
        withCredentials: false,
    });
};

// Controle de refresh single-flight: várias chamadas que recebem 401 ao mesmo
// tempo compartilham a mesma promise de refresh, disparando um único /user/refresh.
let refreshPromise: Promise<string> | null = null;

const doRefresh = async (): Promise<string> => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
        throw new Error("Refresh token ausente");
    }

    // Usa a instância pública (sem interceptor) para não cair em loop de 401.
    const { data } = await getPublicApi().post<RefreshResponse>("/user/refresh", {
        refresh_token: refreshToken,
    });

    persistToken(data.access_token);
    persistRefreshToken(data.refresh_token);

    return data.access_token;
};

const refreshAccessToken = (): Promise<string> => {
    if (!refreshPromise) {
        refreshPromise = doRefresh();
        // Libera o mutex após resolver ou rejeitar, sem gerar uma promise
        // flutuante rejeitada (por isso usamos .then(clear, clear) e não .finally).
        const clear = () => {
            refreshPromise = null;
        };
        refreshPromise.then(clear, clear);
    }

    return refreshPromise;
};

export const getPrivateApi = () => {
    const instance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
        },
    });

    instance.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem("authToken");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config as
                | (AxiosRequestConfig & { _retry?: boolean })
                | undefined;

            // O backend responde 401 (token expirado) ou 403 (token
            // malformado/inválido) quando o access token não serve mais.
            const status = error.response?.status;

            if (
                (status === 401 || status === 403) &&
                originalRequest &&
                !originalRequest._retry
            ) {
                originalRequest._retry = true;

                try {
                    const newToken = await refreshAccessToken();
                    originalRequest.headers = {
                        ...originalRequest.headers,
                        Authorization: `Bearer ${newToken}`,
                    };
                    return instance(originalRequest);
                } catch (refreshError) {
                    clearSession();
                    window.location.assign("/login");
                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

export const getBaseURL = () => {
    return process.env.REACT_APP_API_URL;
};
